import React, { useState, useCallback, useRef } from 'react';
import {
  runReconciliation, runCsvReconciliation,
  approveMatch, explainMatch, getBankAccounts,
  downloadReconciliationCsv, createRule
} from '../services/api';
import MatchCard from './MatchCard';
import StatsBar from './StatsBar';
import InvoicePicker from './InvoicePicker';

const TABS = ['pending', 'posted', 'unmatched', 'ignored'];

export default function ReconcileView({ tenantId, clientName }) {
  const [mode, setMode] = useState('xero'); // 'xero' | 'csv'
  const [state, setState] = useState({
    status: 'idle',
    matches: [],
    summary: null,
    runAt: null,
    error: null,
    transactionCount: 0,
    invoiceCount: 0,
    ruleMatchCount: 0,
    deterministicMatchCount: 0,
    aiMatchCount: 0,
    jobId: null,
    warnings: []
  });
  const [overrides, setOverrides] = useState({}); // bankTransactionId → { invoice } or { ignored: true }
  const [pickingFor, setPickingFor] = useState(null); // bankTransactionId currently being re-assigned
  const [posting, setPosting] = useState(new Set()); // bankTransactionIds with an in-flight approve call
  const [bulkPosting, setBulkPosting] = useState(false); // "Post all confident" in progress
  const [statementFile, setStatementFile] = useState(null);
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [posted, setPosted] = useState(new Set());
  const [activeTab, setActiveTab] = useState('pending');
  const [filter, setFilter] = useState('all');
  const [loadingExplain, setLoadingExplain] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedBankAccount, setSelectedBankAccount] = useState('');
  const statementInputRef = useRef(null);
  const invoiceInputRef = useRef(null);

  React.useEffect(() => {
    if (mode !== 'xero') return;
    getBankAccounts(tenantId)
      .then(accounts => {
        setBankAccounts(accounts || []);
        if (accounts?.length === 1) setSelectedBankAccount(accounts[0].id);
      })
      .catch(() => {});
  }, [tenantId, mode]);

  const resetResults = () => {
    setState(s => ({ ...s, status: 'idle', matches: [], error: null, jobId: null, warnings: [] }));
    setPosted(new Set());
    setOverrides({});
  };

  const onStatementFile = useCallback((file) => {
    if (!file) return;
    setStatementFile(file);
    resetResults();
  }, []);

  const onInvoiceFile = useCallback((file) => {
    if (!file) return;
    setInvoiceFile(file);
    resetResults();
  }, []);

  const handleDrop = (handler) => (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) handler(file);
  };

  const run = useCallback(async () => {
    if (!statementFile) return;
    if (mode === 'csv' && !invoiceFile) return;

    // Clear stale results so they don't render through the running banner.
    setState(s => ({
      ...s, status: 'running', error: null,
      matches: [], summary: null, warnings: [],
      invoiceCount: 0, ruleMatchCount: 0,
      deterministicMatchCount: 0, aiMatchCount: 0
    }));
    setPosted(new Set());
    setOverrides({});

    try {
      const onProgress = (progress) => {
        if (progress.invoiceCount) {
          setState(s => ({ ...s, invoiceCount: progress.invoiceCount }));
        }
        if (progress.deterministicMatches !== undefined && progress.deterministicMatches !== null) {
          setState(s => ({ ...s, deterministicMatchCount: progress.deterministicMatches }));
        }
      };

      const data = mode === 'csv'
        ? await runCsvReconciliation(statementFile, invoiceFile, onProgress)
        : await runReconciliation(tenantId, statementFile, onProgress);

      setState({
        status: 'done',
        matches: data.matches || [],
        summary: data.summary,
        runAt: data.runAt,
        transactionCount: data.transactionCount || 0,
        invoiceCount: data.invoiceCount || 0,
        ruleMatchCount: data.ruleMatchCount || 0,
        deterministicMatchCount: data.deterministicMatchCount || 0,
        aiMatchCount: data.aiMatchCount || 0,
        jobId: data.jobId || null,
        warnings: data.warnings || [],
        error: null
      });
    } catch (err) {
      const serverMessage = err.response?.data?.error || err.response?.data?.message;
      setState(s => ({ ...s, status: 'error', error: serverMessage || err.message || 'Something went wrong' }));
    }
  }, [mode, tenantId, statementFile, invoiceFile]);

  // Apply local overrides BEFORE any callback that needs them, otherwise
  // the useCallback dependency arrays hit a temporal-dead-zone reference.
  const matchesWithOverrides = React.useMemo(() => {
    return state.matches.map(m => {
      const o = overrides[m.bankTransactionId];
      if (!o) return m;
      if (o.ignored) {
        return { ...m, matchType: 'ignored', invoice: null, invoices: [],
                 reasoning: 'Manually marked as ignored.', flags: [], source: 'manual' };
      }
      if (o.invoice) {
        return { ...m, matchType: 'exact', confidence: 100,
                 invoice: o.invoice, invoices: [o.invoice], invoiceId: o.invoice.id,
                 invoiceIds: null, reasoning: 'Manually re-assigned by user.',
                 flags: m.flags || [], source: 'manual' };
      }
      return m;
    });
  }, [state.matches, overrides]);

  // All invoices currently loaded into this run (for the picker)
  const allInvoices = React.useMemo(() => {
    const seen = new Map();
    for (const m of state.matches) {
      for (const inv of m.invoices || []) {
        if (inv?.id && !seen.has(inv.id)) seen.set(inv.id, inv);
      }
    }
    return [...seen.values()];
  }, [state.matches]);

  const handleApprove = useCallback(async (match) => {
    if (mode === 'csv') return; // CSV mode has no Xero posting
    if (!selectedBankAccount) {
      alert('Please select a bank account before approving matches.');
      return;
    }
    // Idempotency guard — a double-click on Post would otherwise create
    // a duplicate payment in Xero. Reject if there's already a request
    // in flight for this transaction.
    if (posting.has(match.bankTransactionId)) return;
    if (posted.has(match.bankTransactionId)) return;

    const invoiceList = match.invoices && match.invoices.length > 0
      ? match.invoices
      : match.invoice ? [match.invoice] : [];
    if (invoiceList.length === 0) return;

    setPosting(p => new Set([...p, match.bankTransactionId]));
    try {
      if (invoiceList.length === 1) {
        const inv = invoiceList[0];
        const result = await approveMatch(
          tenantId, inv.id, match.transaction.amount, selectedBankAccount,
          match.transaction.date, inv.amountDue, inv.contactId, inv.type,
          { jobId: state.jobId, invoiceNumber: inv.invoiceNumber, contactName: inv.contactName }
        );
        setPosted(p => new Set([...p, match.bankTransactionId]));
        if (result?.overpayment) {
          const diff = (parseFloat(match.transaction.amount) - parseFloat(inv.amountDue || 0)).toFixed(2);
          if (result.overpayment.error) {
            // The invoice payment posted, but the overpayment leg failed.
            // Without surfacing this the bookkeeping ends up partially
            // applied and the user has no way to know.
            alert(
              `Invoice payment posted to Xero, BUT the overpayment of £${diff} could NOT be created automatically:\n\n` +
              `${result.overpayment.error}\n\n` +
              `Please create the overpayment manually in Xero (likely cause: account code "800" is not in this org's chart of accounts).`
            );
          } else {
            alert(`Posted to Xero. An overpayment of £${diff} was also created and applied to ${inv.contactName}.`);
          }
        }
      } else {
        // Multi-invoice: pay each invoice for its outstanding amount, and
        // surface any residual that lands on the last invoice as an overpayment.
        const bankAmount = parseFloat(match.transaction.amount);
        const invoicesTotal = invoiceList.reduce((s, i) => s + (parseFloat(i.amountDue) || 0), 0);
        let remaining = bankAmount;
        for (let i = 0; i < invoiceList.length; i++) {
          const inv = invoiceList[i];
          const isLast = i === invoiceList.length - 1;
          const payAmount = isLast ? remaining : Math.min(remaining, parseFloat(inv.amountDue || 0));
          if (payAmount <= 0) continue;
          await approveMatch(
            tenantId, inv.id, payAmount, selectedBankAccount,
            match.transaction.date, inv.amountDue, inv.contactId, inv.type,
            { jobId: state.jobId, invoiceNumber: inv.invoiceNumber, contactName: inv.contactName }
          );
          remaining -= payAmount;
        }
        setPosted(p => new Set([...p, match.bankTransactionId]));
        const residual = bankAmount - invoicesTotal;
        if (residual > 0.01) {
          alert(`Posted ${invoiceList.length} invoices to Xero. An overpayment of £${residual.toFixed(2)} was also created on ${invoiceList[invoiceList.length - 1].contactName}.`);
        }
      }
    } catch (err) {
      const status = err.response?.status;
      const message = err.response?.data?.error || err.message;
      if (status === 401 || (message && message.includes('session'))) {
        alert('Your Xero session has expired. Please refresh the page and log in again.');
        // Re-throw auth failures so bulk-post aborts on the first one
        // instead of spamming the user with N identical alerts.
        throw err;
      } else {
        alert(`Failed to post to Xero: ${message}`);
      }
    } finally {
      setPosting(p => {
        const next = new Set(p);
        next.delete(match.bankTransactionId);
        return next;
      });
    }
  }, [mode, tenantId, selectedBankAccount, state.jobId, posting, posted]);

  // Approve every confident, postable match using the user's current
  // overrides. "Coded" matches are excluded — they have no invoice to
  // post against (they're rule-coded line items, not invoice payments).
  const handleApproveAll = useCallback(async () => {
    if (bulkPosting) return;
    const confident = matchesWithOverrides.filter(m =>
      m.matchType === 'exact' &&
      m.confidence >= 85 &&
      !posted.has(m.bankTransactionId) &&
      !posting.has(m.bankTransactionId) &&
      ((m.invoices && m.invoices.length > 0) || m.invoice)
    );
    if (!confident.length) return;
    setBulkPosting(true);
    try {
      for (const match of confident) {
        try {
          await handleApprove(match);
        } catch (err) {
          // handleApprove re-throws on auth failures — bail out of the
          // bulk loop rather than alerting the user once per match.
          const status = err.response?.status;
          if (status === 401) break;
          // Any other error: handleApprove already alerted; continue.
        }
      }
    } finally {
      setBulkPosting(false);
    }
  }, [bulkPosting, matchesWithOverrides, posted, posting, handleApprove]);

  const handleExplain = useCallback(async (match) => {
    setLoadingExplain(match.bankTransactionId);
    try {
      // For multi-invoice matches, pass the first invoice as the
      // explanation context — sending the whole list confuses the prompt.
      const invForExplain = match.invoice
        || (match.invoices && match.invoices[0])
        || null;
      const explanation = await explainMatch(match.transaction, invForExplain);
      alert(explanation);
    } catch (err) {
      alert('Could not get explanation. Please try again.');
    } finally {
      setLoadingExplain(null);
    }
  }, []);

  const handleExport = useCallback(async () => {
    if (!state.jobId) return;
    try {
      await downloadReconciliationCsv(state.jobId);
    } catch (err) {
      alert(`Couldn't download the CSV: ${err.message}`);
    }
  }, [state.jobId]);

  const handleIgnore = useCallback((match) => {
    setOverrides(o => ({ ...o, [match.bankTransactionId]: { ignored: true } }));
  }, []);

  // Clear an override (undo Ignore, undo Change invoice).
  const handleUndoOverride = useCallback((match) => {
    setOverrides(o => {
      const next = { ...o };
      delete next[match.bankTransactionId];
      return next;
    });
  }, []);

  const handlePickInvoice = useCallback(async (newInvoice, { saveAsRule, rulePhrase }) => {
    if (!pickingFor) return;
    setOverrides(o => ({ ...o, [pickingFor]: { invoice: newInvoice } }));

    if (saveAsRule && newInvoice?.contactName) {
      const txn = state.matches.find(m => m.bankTransactionId === pickingFor)?.transaction;
      // Use the user-supplied phrase (from the modal) — falls back to a
      // distinctive token in the description (longest word ≥ 4 chars,
      // skipping common boilerplate). Previously we silently used the
      // first two words, which made useless rules like "PAYMENT FROM".
      const phrase = (rulePhrase || extractDistinctivePhrase(txn?.description || '')).trim();
      if (!phrase) {
        alert(`Couldn't auto-create a rule — no distinctive phrase found in "${txn?.description || ''}". Override applied for this run only; add a rule manually from the Rules page if you want to automate future ones.`);
      } else {
        try {
          await createRule({
            name: `Auto: ${phrase} → ${newInvoice.contactName}`,
            match_field: 'description',
            match_type: 'contains',
            match_value: phrase,
            // 'code' rule with contact_name only — runs as pass 0 and
            // marks the transaction as a contact-suggested coding hint
            // for future runs.
            action_type: 'code',
            contact_name: newInvoice.contactName,
            notes: `Created from a manual re-assignment on ${new Date().toISOString().slice(0,10)}.`,
            enabled: 1
          });
        } catch (err) {
          alert(`Couldn't save rule: ${err.response?.data?.error || err.message}`);
        }
      }
    }
    setPickingFor(null);
  }, [pickingFor, state.matches]);

  // Derived data — drives the tabs and filter chips
  const pendingMatches = matchesWithOverrides.filter(
    m => m.matchType !== 'unmatched' && m.matchType !== 'ignored' &&
         !posted.has(m.bankTransactionId)
  );
  const unmatchedMatches = matchesWithOverrides.filter(m => m.matchType === 'unmatched');
  const ignoredMatches = matchesWithOverrides.filter(m => m.matchType === 'ignored');
  const postedMatches = matchesWithOverrides.filter(m => posted.has(m.bankTransactionId));
  const filteredPending = filter === 'all' ? pendingMatches
    : filter === 'exact' ? pendingMatches.filter(m => m.matchType === 'exact' || m.matchType === 'coded')
    : pendingMatches.filter(m => m.matchType === 'partial');
  // Only count truly postable confident matches — `coded` rule matches
  // have no invoice and would be silently skipped by handleApproveAll.
  const confidentCount = pendingMatches.filter(m =>
    m.matchType === 'exact' &&
    m.confidence >= 85 &&
    ((m.invoices && m.invoices.length > 0) || m.invoice)
  ).length;

  const canRun = statementFile && (mode === 'xero' || invoiceFile);

  return (
    <div className="reconcile-view">
      {/* Mode toggle */}
      <div className="mode-toggle">
        <button
          className={`mode-btn ${mode === 'xero' ? 'active' : ''}`}
          onClick={() => { setMode('xero'); resetResults(); }}
          type="button"
        >
          Match against Xero
        </button>
        <button
          className={`mode-btn ${mode === 'csv' ? 'active' : ''}`}
          onClick={() => { setMode('csv'); resetResults(); }}
          type="button"
        >
          Match against invoice CSV
        </button>
      </div>

      {/* Upload + controls */}
      <div className="controls-bar">
        <div className="controls-left">
          <UploadSlot
            label="bank statement (CSV / XLSX)"
            file={statementFile}
            inputRef={statementInputRef}
            onFile={onStatementFile}
            onDrop={handleDrop(onStatementFile)}
          />

          {mode === 'csv' && (
            <UploadSlot
              label="invoice list (CSV / XLSX)"
              file={invoiceFile}
              inputRef={invoiceInputRef}
              onFile={onInvoiceFile}
              onDrop={handleDrop(onInvoiceFile)}
            />
          )}

          {mode === 'xero' && bankAccounts.length > 1 && (
            <select
              className="days-select"
              value={selectedBankAccount}
              onChange={e => setSelectedBankAccount(e.target.value)}
            >
              <option value="">Select bank account…</option>
              {bankAccounts.map(a => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          )}

          {state.runAt && (
            <span className="run-at">
              Last run {new Date(state.runAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
        </div>

        <div className="controls-right">
          {state.status === 'done' && state.jobId && (
            <button className="btn-secondary" onClick={handleExport}>
              Export CSV
            </button>
          )}
          {mode === 'xero' && confidentCount > 0 && state.status === 'done' && (
            <button
              className="btn-secondary"
              onClick={handleApproveAll}
              disabled={bulkPosting}
            >
              {bulkPosting
                ? `Posting ${posting.size}…`
                : `Post all confident (${confidentCount})`}
            </button>
          )}
          <button
            className="btn-primary"
            onClick={run}
            disabled={state.status === 'running' || !canRun}
          >
            {state.status === 'running' ? 'Running...' : state.status === 'idle' ? 'Run reconciliation' : 'Re-run'}
          </button>
        </div>
      </div>

      {/* Running state */}
      {state.status === 'running' && (
        <div className="running-state">
          <div className="spinner" />
          <div>
            <p className="running-title">Matching your transactions...</p>
            <p className="running-sub">
              {mode === 'csv'
                ? (state.invoiceCount
                    ? `Matching ${state.transactionCount} bank transactions against ${state.invoiceCount} invoices...`
                    : `Parsing files...`)
                : (state.invoiceCount
                    ? `Matching ${state.transactionCount} bank transactions against ${state.invoiceCount} Xero invoices...`
                    : `Fetching invoices from Xero for ${state.transactionCount} bank transactions...`)
              }
            </p>
            <p className="running-sub" style={{ marginTop: 6, opacity: 0.7 }}>
              Deterministic match runs first (instant). AI matches the leftovers — that can take a couple of minutes for large statements.
            </p>
          </div>
        </div>
      )}

      {/* Error */}
      {state.status === 'error' && (
        <div className="error-state">
          <p className="error-title">Something went wrong</p>
          <p className="error-msg">{state.error}</p>
          <button className="btn-primary" onClick={run}>Try again</button>
        </div>
      )}

      {/* Idle */}
      {state.status === 'idle' && (
        <div className="idle-state">
          {canRun ? (
            <>
              <p className="idle-title">
                Ready to reconcile {mode === 'xero' ? clientName : 'using uploaded files'}
              </p>
              <p className="idle-sub">
                {mode === 'csv'
                  ? `${statementFile.name} + ${invoiceFile.name} uploaded. Click "Run reconciliation" to start matching.`
                  : `${statementFile.name} uploaded. Click "Run reconciliation" to start matching.`}
              </p>
              {mode === 'xero' && bankAccounts.length > 1 && !selectedBankAccount && (
                <p className="idle-sub" style={{ color: '#BA7517' }}>
                  ⚠ Please select a bank account above before running.
                </p>
              )}
            </>
          ) : (
            <>
              <p className="idle-title">
                {mode === 'csv'
                  ? 'Upload a bank statement AND an invoice list'
                  : `Upload a bank statement to reconcile ${clientName}`}
              </p>
              <p className="idle-sub">
                {mode === 'csv'
                  ? 'Export your outstanding invoices from your bookkeeping system as a CSV or XLSX (must include invoice number, contact, amount due, and date), then upload it together with your bank statement.'
                  : 'Download your bank statement as a CSV or XLSX from your online banking portal, then drag & drop or click to upload the file above.'}
              </p>
            </>
          )}
        </div>
      )}

      {/* Warnings from statement validation */}
      {state.warnings && state.warnings.length > 0 && (
        <div className="warnings-panel">
          {state.warnings.map((w, idx) => (
            <div key={idx} className={`warning warning-${w.severity}`}>
              <span className="warning-icon">
                {w.severity === 'error' ? '✕' : w.severity === 'warning' ? '⚠' : 'ⓘ'}
              </span>
              <span className="warning-msg">{w.message}</span>
            </div>
          ))}
        </div>
      )}

      {/* Results */}
      {state.status === 'done' && (
        <>
          <StatsBar summary={state.summary} postedCount={posted.size} mode={mode} />

          {(state.ruleMatchCount > 0 || state.deterministicMatchCount > 0 || state.aiMatchCount > 0) && (
            <div className="match-source-breakdown">
              {state.ruleMatchCount > 0 && `${state.ruleMatchCount} matched by your rules · `}
              {state.deterministicMatchCount} matched by deterministic rules
              {state.aiMatchCount > 0 && ` · ${state.aiMatchCount} matched by AI`}
            </div>
          )}

          <div className="tabs">
            {TABS.map(tab => (
              <button
                key={tab}
                className={`tab ${activeTab === tab ? 'active' : ''}`}
                onClick={() => setActiveTab(tab)}
              >
                {tab === 'pending' && `Pending (${filteredPending.length})`}
                {tab === 'posted' && `Posted (${postedMatches.length})`}
                {tab === 'unmatched' && `Unmatched (${unmatchedMatches.length})`}
                {tab === 'ignored' && `Ignored (${ignoredMatches.length})`}
              </button>
            ))}
          </div>

          {activeTab === 'pending' && (
            <div className="filter-chips">
              {['all', 'exact', 'partial'].map(f => (
                <button
                  key={f}
                  className={`chip ${filter === f ? 'active' : ''}`}
                  onClick={() => setFilter(f)}
                >
                  {f === 'all' ? 'All' : f === 'exact' ? 'Confident' : 'Needs review'}
                </button>
              ))}
            </div>
          )}

          <div className="match-list">
            {activeTab === 'pending' && filteredPending.map(match => (
              <MatchCard
                key={match.bankTransactionId}
                match={match}
                onApprove={mode === 'xero' && match.matchType !== 'coded' ? () => handleApprove(match) : null}
                onChangeInvoice={allInvoices.length > 0 ? () => setPickingFor(match.bankTransactionId) : null}
                onIgnore={() => handleIgnore(match)}
                onExplain={() => handleExplain(match)}
                explainLoading={loadingExplain === match.bankTransactionId}
                approveLoading={posting.has(match.bankTransactionId)}
              />
            ))}

            {activeTab === 'posted' && postedMatches.map(match => (
              <MatchCard key={match.bankTransactionId} match={match} isPosted />
            ))}

            {activeTab === 'unmatched' && unmatchedMatches.map(match => (
              <MatchCard
                key={match.bankTransactionId}
                match={match}
                onChangeInvoice={allInvoices.length > 0 ? () => setPickingFor(match.bankTransactionId) : null}
                onIgnore={() => handleIgnore(match)}
                onExplain={() => handleExplain(match)}
                explainLoading={loadingExplain === match.bankTransactionId}
              />
            ))}

            {activeTab === 'ignored' && ignoredMatches.map(match => (
              <MatchCard
                key={match.bankTransactionId}
                match={match}
                onUndo={() => handleUndoOverride(match)}
              />
            ))}

            {activeTab === 'pending' && filteredPending.length === 0 && (
              <div className="empty-tab">No transactions in this category</div>
            )}
            {activeTab === 'ignored' && ignoredMatches.length === 0 && (
              <div className="empty-tab">Nothing ignored.</div>
            )}
          </div>
        </>
      )}

      {/* Invoice picker modal */}
      {pickingFor && (
        <InvoicePicker
          invoices={allInvoices}
          transaction={matchesWithOverrides.find(m => m.bankTransactionId === pickingFor)?.transaction}
          onPick={handlePickInvoice}
          onCancel={() => setPickingFor(null)}
          onCreateRule={true}
        />
      )}
    </div>
  );
}

// Extract the most distinctive word from a bank description for use as a
// default rule pattern. Skips short and boilerplate words ("PAYMENT",
// "TRANSFER", numbers, etc) and prefers the longest remaining token.
const BOILERPLATE = new Set([
  'PAYMENT', 'PAYMENTS', 'TRANSFER', 'FROM', 'TO', 'THE', 'AND', 'BANK',
  'LTD', 'LIMITED', 'LLC', 'INC', 'PLC', 'BY', 'FOR', 'OF', 'CO',
  'BACS', 'CHAPS', 'FPS', 'DD', 'SO', 'REF', 'ID'
]);
function extractDistinctivePhrase(description) {
  if (!description) return '';
  const words = description.toUpperCase().split(/\s+/)
    .map(w => w.replace(/[^A-Z0-9]/g, ''))
    .filter(w => w.length >= 4 && !BOILERPLATE.has(w) && !/^\d+$/.test(w));
  if (!words.length) return '';
  // Pick the longest distinctive word — usually the merchant/contact name.
  words.sort((a, b) => b.length - a.length);
  return words[0];
}

function UploadSlot({ label, file, inputRef, onFile, onDrop }) {
  return (
    <div
      className={`csv-upload-area ${file ? 'has-file' : ''}`}
      onDrop={onDrop}
      onDragOver={e => e.preventDefault()}
      onClick={() => inputRef.current?.click()}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".csv,.xlsx,.xls"
        style={{ display: 'none' }}
        onChange={e => onFile(e.target.files?.[0])}
      />
      {file ? (
        <span className="csv-file-name">📄 {file.name}</span>
      ) : (
        <span className="csv-upload-prompt">
          <span className="csv-upload-icon">↑</span> Upload {label}
        </span>
      )}
    </div>
  );
}
